import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Briefcase, Search, Code2, Database, Network, Shield,
  Cpu, Globe2, Blocks, Brain, LineChart, Users2, ArrowLeft,
  GraduationCap, Heart
} from 'lucide-react';
import Modal from '../components/Modal';

interface Position {
  title: string;
  level: string;
  location: string;
  type: 'paid' | 'internship' | 'volunteer';
  duration?: string;
  stipend?: string;
  benefits?: string[];
}

interface Domain {
  id: string;
  icon: React.ReactElement;
  name: string;
  description: string;
  skills: string[];
  positions: Position[];
}

const domains: Domain[] = [
  {
    id: 'frontend',
    icon: <Code2 className="h-6 w-6 text-blue-500" />,
    name: 'Front-End Engineering',
    description: 'Build seamless user interfaces with modern frameworks and tools',
    skills: ['React', 'Angular', 'Vue.js', 'TypeScript', 'Tailwind CSS'],
    positions: [
      {
        title: 'Senior Front-End Engineer',
        level: 'Senior',
        location: 'Remote (Africa)',
        type: 'paid',
        benefits: ['Health Insurance', 'Remote Work', 'Learning Budget']
      },
      {
        title: 'UI/UX Developer',
        level: 'Mid-level',
        location: 'Remote (Asia)',
        type: 'paid',
        benefits: ['Flexible Hours', 'Remote Work', 'Training Programs']
      },
      {
        title: 'Frontend Developer Intern',
        level: 'Entry',
        location: 'Remote (Global)',
        type: 'internship',
        duration: '6 months',
        stipend: 'Competitive Stipend'
      }
    ]
  },
  {
    id: 'mobile',
    icon: <Cpu className="h-6 w-6 text-green-500" />,
    name: 'Mobile Application Engineering',
    description: 'Develop high-performance mobile apps for iOS and Android',
    skills: ['Swift', 'Kotlin', 'React Native', 'Flutter'],
    positions: [
      {
        title: 'Mobile App Developer',
        level: 'Mid-level',
        location: 'Remote (Global)',
        type: 'paid',
        benefits: ['Health Insurance', 'Remote Work', 'Learning Budget']
      },
      {
        title: 'Mobile Development Volunteer',
        level: 'Entry',
        location: 'Remote (Africa)',
        type: 'volunteer',
        duration: '3 months',
        benefits: ['Mentorship', 'Certificate', 'Project Experience']
      }
    ]
  },
  {
    id: 'backend',
    icon: <Database className="h-6 w-6 text-purple-500" />,
    name: 'Back-End Engineering',
    description: 'Build scalable server-side applications and APIs',
    skills: ['Node.js', 'Python', 'Java', 'PostgreSQL', 'MongoDB'],
    positions: [
      {
        title: 'Backend Engineer',
        level: 'Senior',
        location: 'Remote (South America)',
        type: 'paid',
        benefits: ['Health Insurance', 'Remote Work', 'Learning Budget']
      },
      {
        title: 'Backend Development Intern',
        level: 'Entry',
        location: 'Remote (Asia)',
        type: 'internship',
        duration: '6 months',
        stipend: 'Competitive Stipend'
      }
    ]
  },
  {
    id: 'devops',
    icon: <Network className="h-6 w-6 text-red-500" />,
    name: 'Cloud & DevOps Engineering',
    description: 'Manage cloud infrastructure and deployment pipelines',
    skills: ['AWS', 'Docker', 'Kubernetes', 'Terraform'],
    positions: [
      {
        title: 'DevOps Engineer',
        level: 'Mid-level',
        location: 'Remote (Asia)',
        type: 'paid',
        benefits: ['Health Insurance', 'Remote Work', 'Learning Budget']
      },
      {
        title: 'Cloud Infrastructure Volunteer',
        level: 'Entry',
        location: 'Remote (Global)',
        type: 'volunteer',
        duration: '3 months',
        benefits: ['Mentorship', 'Certificate', 'Project Experience']
      }
    ]
  },
  {
    id: 'security',
    icon: <Shield className="h-6 w-6 text-yellow-500" />,
    name: 'Cybersecurity Engineering',
    description: 'Protect systems and data from security threats',
    skills: ['Penetration Testing', 'Security Auditing', 'Incident Response'],
    positions: [
      {
        title: 'Security Engineer',
        level: 'Senior',
        location: 'Remote (Global)',
        type: 'paid',
        benefits: ['Health Insurance', 'Remote Work', 'Learning Budget']
      },
      {
        title: 'Security Research Intern',
        level: 'Entry',
        location: 'Remote (Africa)',
        type: 'internship',
        duration: '6 months',
        stipend: 'Competitive Stipend'
      }
    ]
  },
  {
    id: 'blockchain',
    icon: <Blocks className="h-6 w-6 text-indigo-500" />,
    name: 'Blockchain Engineering',
    description: 'Develop decentralized applications and smart contracts',
    skills: ['Solidity', 'Web3.js', 'Smart Contracts', 'DeFi'],
    positions: [
      {
        title: 'Blockchain Developer',
        level: 'Mid-level',
        location: 'Remote (Africa)',
        type: 'paid',
        benefits: ['Health Insurance', 'Remote Work', 'Learning Budget']
      },
      {
        title: 'Blockchain Development Volunteer',
        level: 'Entry',
        location: 'Remote (Asia)',
        type: 'volunteer',
        duration: '3 months',
        benefits: ['Mentorship', 'Certificate', 'Project Experience']
      }
    ]
  },
  {
    id: 'ai',
    icon: <Brain className="h-6 w-6 text-pink-500" />,
    name: 'AI & Machine Learning',
    description: 'Build and deploy AI models and algorithms',
    skills: ['Python', 'TensorFlow', 'PyTorch', 'NLP'],
    positions: [
      {
        title: 'ML Engineer',
        level: 'Senior',
        location: 'Remote (Asia)',
        type: 'paid',
        benefits: ['Health Insurance', 'Remote Work', 'Learning Budget']
      },
      {
        title: 'AI Research Intern',
        level: 'Entry',
        location: 'Remote (Global)',
        type: 'internship',
        duration: '6 months',
        stipend: 'Competitive Stipend'
      }
    ]
  },
  {
    id: 'data',
    icon: <LineChart className="h-6 w-6 text-cyan-500" />,
    name: 'Data Engineering',
    description: 'Design and maintain data infrastructure',
    skills: ['SQL', 'Apache Spark', 'ETL', 'Data Warehousing'],
    positions: [
      {
        title: 'Data Engineer',
        level: 'Mid-level',
        location: 'Remote (Global)',
        type: 'paid',
        benefits: ['Health Insurance', 'Remote Work', 'Learning Budget']
      },
      {
        title: 'Data Engineering Volunteer',
        level: 'Entry',
        location: 'Remote (South America)',
        type: 'volunteer',
        duration: '3 months',
        benefits: ['Mentorship', 'Certificate', 'Project Experience']
      }
    ]
  }
];

export default function CareersPage() {
  const { theme } = useTheme();
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<{
    domain: string;
    position: Position;
  } | null>(null);
  const [applicationData, setApplicationData] = useState({
    gocityId: '',
    email: '',
    cv: null as File | null,
    type: 'paid' as 'paid' | 'internship' | 'volunteer'
  });

  const handleApply = (domain: string, position: Position) => {
    setSelectedPosition({ domain, position });
    setApplicationData(prev => ({ ...prev, type: position.type }));
    setIsModalOpen(true);
  };

  const handleSubmitApplication = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle application submission
    console.log('Application submitted:', {
      position: selectedPosition,
      ...applicationData
    });
    setIsModalOpen(false);
    setApplicationData({
      gocityId: '',
      email: '',
      cv: null,
      type: 'paid'
    });
  };

  const getPositionTypeIcon = (type: Position['type']) => {
    switch (type) {
      case 'paid':
        return <Briefcase className="h-4 w-4 text-green-500" />;
      case 'internship':
        return <GraduationCap className="h-4 w-4 text-blue-500" />;
      case 'volunteer':
        return <Heart className="h-4 w-4 text-red-500" />;
    }
  };

  const getPositionTypeLabel = (type: Position['type']) => {
    switch (type) {
      case 'paid':
        return 'Full-time Position';
      case 'internship':
        return 'Internship Program';
      case 'volunteer':
        return 'Volunteer Opportunity';
    }
  };

  const getPositionTypeColor = (type: Position['type']) => {
    switch (type) {
      case 'paid':
        return theme === 'dark' ? 'bg-green-500/20 text-green-400' : 'bg-green-50 text-green-600';
      case 'internship':
        return theme === 'dark' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600';
      case 'volunteer':
        return theme === 'dark' ? 'bg-red-500/20 text-red-400' : 'bg-red-50 text-red-600';
    }
  };

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/about"
          className={`
            inline-flex items-center space-x-2 mb-8 group
            ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}
          `}
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          <span>Return to About Us</span>
        </Link>

        <div className="text-center mb-16">
          <div className="flex justify-center mb-4">
            <Briefcase className="h-12 w-12 text-blue-500" />
          </div>
          <h1 className={`text-4xl font-bold mb-4 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Join Our Global Mission
          </h1>
          <p className={`text-xl max-w-3xl mx-auto ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Be part of our initiative to transform the global technology workforce through innovation and opportunity in developing nations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            {
              type: 'paid' as const,
              title: 'Full-time Positions',
              description: 'Join our team in a permanent role with competitive compensation'
            },
            {
              type: 'internship' as const,
              title: 'Internship Programs',
              description: 'Gain valuable experience with mentorship and potential for full-time conversion'
            },
            {
              type: 'volunteer' as const,
              title: 'Volunteer Opportunities',
              description: 'Contribute to meaningful projects while developing your skills'
            }
          ].map(option => (
            <div
              key={option.type}
              className={`
                p-6 rounded-lg
                ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
              `}
            >
              <div className="flex items-center space-x-2 mb-4">
                {getPositionTypeIcon(option.type)}
                <h3 className={`text-lg font-bold ${
                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  {option.title}
                </h3>
              </div>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {option.description}
              </p>
            </div>
          ))}
        </div>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <Search className={`absolute left-3 top-3 h-5 w-5 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              placeholder="Search domains or skills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`
                w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {domains.map((domain) => (
            <div
              key={domain.id}
              onClick={() => setSelectedDomain(selectedDomain === domain.id ? null : domain.id)}
              className={`
                p-6 rounded-lg cursor-pointer transition-all duration-200
                ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                ${selectedDomain === domain.id
                  ? theme === 'dark'
                    ? 'ring-2 ring-blue-500'
                    : 'ring-2 ring-blue-600'
                  : ''
                }
              `}
            >
              <div className="flex items-start space-x-4">
                {domain.icon}
                <div className="flex-1 min-w-0">
                  <h3 className={`text-lg font-bold mb-2 ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    {domain.name}
                  </h3>
                  <p className={`text-sm mb-4 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {domain.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {domain.skills.map((skill) => (
                      <span
                        key={skill}
                        className={`
                          text-xs px-2 py-1 rounded-full
                          ${theme === 'dark'
                            ? 'bg-gray-700 text-gray-300'
                            : 'bg-gray-100 text-gray-700'
                          }
                        `}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {selectedDomain === domain.id && (
                <div className={`
                  mt-6 pt-6 border-t
                  ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}
                `}>
                  <h4 className={`text-sm font-medium mb-4 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Open Positions
                  </h4>
                  <div className="space-y-4">
                    {domain.positions.map((position, index) => (
                      <div
                        key={index}
                        className={`
                          p-4 rounded-lg
                          ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}
                        `}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h5 className={`font-medium ${
                            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            {position.title}
                          </h5>
                          <div className={`
                            flex items-center space-x-1 px-2 py-1 rounded-full text-xs
                            ${getPositionTypeColor(position.type)}
                          `}>
                            {getPositionTypeIcon(position.type)}
                            <span>{getPositionTypeLabel(position.type)}</span>
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2 text-sm">
                          <div className={
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }>
                            {position.location} • {position.level}
                            {position.duration && ` • ${position.duration}`}
                          </div>
                          {position.stipend && (
                            <div className={`font-medium ${
                              theme === 'dark' ? 'text-green-400' : 'text-green-600'
                            }`}>
                              {position.stipend}
                            </div>
                          )}
                          {position.benefits && (
                            <div className="flex flex-wrap gap-2">
                              {position.benefits.map((benefit, i) => (
                                <span
                                  key={i}
                                  className={`
                                    text-xs px-2 py-0.5 rounded-full
                                    ${theme === 'dark'
                                      ? 'bg-gray-600 text-gray-300'
                                      : 'bg-gray-200 text-gray-700'
                                    }
                                  `}
                                >
                                  {benefit}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleApply(domain.name, position)}
                          className={`
                            mt-4 w-full px-4 py-2 rounded-lg transition-colors
                            ${theme === 'dark'
                              ? 'bg-gray-600 text-gray-100 hover:bg-gray-500'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }
                          `}
                        >
                          Apply Now
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={`Apply for ${selectedPosition?.position.title}`}
        >
          <form onSubmit={handleSubmitApplication} className="space-y-6">
            <div className={`
              p-4 rounded-lg mb-6
              ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}
            `}>
              <div className="flex items-center space-x-2 mb-2">
                {selectedPosition && getPositionTypeIcon(selectedPosition.position.type)}
                <span className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  {selectedPosition && getPositionTypeLabel(selectedPosition.position.type)}
                </span>
              </div>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {selectedPosition?.domain} • {selectedPosition?.position.location}
              </p>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}>
                Gocity Job Username / ID
              </label>
              <input
                type="text"
                value={applicationData.gocityId}
                onChange={(e) => setApplicationData(prev => ({ ...prev, gocityId: e.target.value }))}
                className={`
                  w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                  ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}>
                Email Address
              </label>
              <input
                type="email"
                value={applicationData.email}
                onChange={(e) => setApplicationData(prev => ({ ...prev, email: e.target.value }))}
                className={`
                  w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                  ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}>
                Upload CV
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setApplicationData(prev => ({
                  ...prev,
                  cv: e.target.files?.[0] || null
                }))}
                className={`
                  w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                  ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  ${theme === 'dark'
                    ? 'file:bg-gray-600 file:text-gray-100'
                    : 'file:bg-gray-100 file:text-gray-700'
                  }
                  hover:file:bg-gray-200
                `}
                required
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className={`
                  px-4 py-2 rounded-lg transition-colors
                  ${theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Submit Application
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
}